import { ComponentType, ReactNode } from "react";

export enum DocumentUploadModalMode {
  ADD = "ADD",
  EDIT = "EDIT",
  ADD_LOMN = "ADD_LOMN",
  ADD_NOBC = "ADD_NOBC",
  ADD_LAB_RESULTS = "ADD_LAB_RESULTS",
  ADD_DENIAL_LETTER = "ADD_DENIAL_LETTER",
}

export enum DocumentUploadModalNavigationDirection {
  BACK = "BACK",
  CLOSE = "CLOSE",
}

export enum DocumentUploadModalView {
  CHOOSE_DOCUMENT_TYPE = "CHOOSE_DOCUMENT_TYPE",
  UPLOAD_DOCUMENT = "UPLOAD_DOCUMENT",
  VIEW_FILE = "VIEW_FILE",
  DELETE_CONFIRMATION = "DELETE_CONFIRMATION",
  CLOSE_CONFIRMATION = "CLOSE_CONFIRMATION",
}

export enum AppealSupportingDocumentType {
  NoticeOfBenefitChange = "NOTICE_OF_BENEFIT_CHANGE",
  DenialLetter = "DENIAL_LETTER",
  LetterOfMedicalNecessity = "LETTER_OF_MEDICAL_NECESSITY",
  CoverLetter = "COVER_LETTER",
  LabResults = "LAB_RESULTS",
  MedicalRecords = "MEDICAL_RECORDS",
  InsuranceForm = "INSURANCE_FORM",
  Other = "OTHER",
  PatientConsent = "PATIENT_CONSENT",
  InsuranceCard = "INSURANCE_CARD",
  DenialUpheldLetter = "DENIAL_UPHELD_LETTER",
  ApprovalLetter = "APPROVAL_LETTER",
}

export enum AppealSupportingDocumentFileType {
  Pdf = "PDF",
  Image = "IMAGE",
}

export type AppealSupportingDocumentFile = {
  fileId: string;
  fileName: string;
  fileType: AppealSupportingDocumentFileType;
  url: string;
  pageCount: number;
};

export type AppealSupportingDocument = {
  documentId: string;
  documentType: AppealSupportingDocumentType;
  otherDocumentTypeName?: string | null;
  files: AppealSupportingDocumentFile[];
};

export type ChooseDocumentTypeViewState = {
  view: DocumentUploadModalView.CHOOSE_DOCUMENT_TYPE;
};

export type UploadDocumentViewState = {
  view: DocumentUploadModalView.UPLOAD_DOCUMENT;
  documentType: AppealSupportingDocumentType;
  otherDocumentName: string | null;
};

export type ViewFileViewState = {
  view: DocumentUploadModalView.VIEW_FILE;
  file: File;
};

export type DeleteConfirmationViewState = {
  view: DocumentUploadModalView.DELETE_CONFIRMATION;
  file: File;
};

export type CloseConfirmationViewState = {
  view: DocumentUploadModalView.CLOSE_CONFIRMATION;
  navigationDirection: DocumentUploadModalNavigationDirection;
};

export type ModalViewState =
  | ChooseDocumentTypeViewState
  | UploadDocumentViewState
  | ViewFileViewState
  | DeleteConfirmationViewState
  | CloseConfirmationViewState;

export type GenerateUploadUrlsFile = {
  fileName: string;
  fileType: AppealSupportingDocumentFileType;
  pageCount: number;
};

export type GenerateUploadUrlsInput = {
  documentType: AppealSupportingDocumentType;
  otherDocumentTypeName?: string;
  files: GenerateUploadUrlsFile[];
};

export type GenerateUploadUrlsFileDetail = {
  fileId: string;
  fileName: string;
  uploadUrl: string;
};

export type GenerateUploadUrlsResult = {
  documentId: string;
  fileUploadDetails: GenerateUploadUrlsFileDetail[];
};

export type UpsertDocumentFile = GenerateUploadUrlsFile & {
  fileId: string;
};

export type UpsertDocumentInput = {
  documentId: string;
  documentType: AppealSupportingDocumentType;
  otherDocumentTypeName?: string;
  files: UpsertDocumentFile[];
};

export type ButtonSlotProps = {
  variant: "primary" | "secondary" | "tertiary";
  size: "small" | "medium";
  onClick?: () => void;
  disabled?: boolean;
  loading?: boolean;
  isFullWidth?: boolean;
  tone?: "destructive";
  type?: "button" | "submit" | "reset";
  children?: ReactNode;
};

export type UploadFileInputSlotProps = {
  onFilesSelected: (files: File[]) => void;
  onError: (message: string) => void;
  accept: string[];
  multiple: boolean;
  disabled?: boolean;
  files?: File[];
  onViewFile?: (file: File) => void;
  onFileDeleted?: (file: File) => void;
};

export type DocumentUploadModalConfig = {
  additionalModes?: Array<{
    mode: DocumentUploadModalMode;
    documentType: AppealSupportingDocumentType;
  }>;
  excludedDocumentTypes: AppealSupportingDocumentType[];
  documentTypeDescriptions?: Partial<
    Record<AppealSupportingDocumentType, string>
  >;
};

export type DocumentUploadModalDeps = {
  getFileUrlsForFile: (file: File) => Promise<string[]>;
  downloadFile: (url: string) => Promise<File>;
  uploadFileToUrl: (file: File, url: string) => Promise<void>;
  generateUploadUrls: (
    input: GenerateUploadUrlsInput,
  ) => Promise<GenerateUploadUrlsResult>;
  upsertDocument: (input: UpsertDocumentInput) => Promise<void>;
  deleteDocument: (documentId: string) => Promise<void>;
  getClientSafeErrorMessage?: (error: unknown) => string | null;
};

export type DocumentUploadModalSlots = {
  Button: ComponentType<ButtonSlotProps>;
  UploadFileInput: ComponentType<UploadFileInputSlotProps>;
};

export type GenerateTemporaryUploadResult = {
  uploadToken: string;
  downloadUrl: string;
};

export type QrUploadConfig = {
  phoneUploadPath: string;
  generateTemporaryUpload: (
    appealId: string,
    documentType: AppealSupportingDocumentType,
  ) => Promise<GenerateTemporaryUploadResult>;
};

export type DocumentUploadModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmitDocument: () => void;
  mode: DocumentUploadModalMode;
  document: AppealSupportingDocument | null;
  onLoadingDocumentFilesError: () => void;
  appealId: string;
  config: DocumentUploadModalConfig;
  deps: DocumentUploadModalDeps;
  slots: DocumentUploadModalSlots;
  qrUpload?: QrUploadConfig;
};
